import {
  Brain,
  Check,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import {
  useApplyLearnedBaseline,
  useCreatePricingRule,
  useCurrentUser,
  useDeletePricingRule,
  useDismissLearnedObservations,
  useLearningSuggestions,
  useOrganizations,
  usePricingRules,
  useResetPricingRulesToDefaults,
  useSeedMissingPricingRuleDefaults,
  useUpdatePricingRule,
  type LearningSuggestion,
} from "@/api/hooks";
import type { OrganizationSummary, PricingRuleResponse, PricingTier } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HelpPopover } from "@/components/HelpPopover";
import { formatCents, shortId } from "@/lib/format";

const TIER_LABELS: Record<PricingTier, string> = {
  builder: "Builder grade",
  mid: "Mid-range",
  premium: "Premium",
  luxury: "Luxury",
};

export function PricingRulesPage() {
  const me = useCurrentUser();
  const myRole = me.data?.role;
  const isPencilOutAdmin = myRole === "pencilout_admin";
  const canManage = myRole === "pencilout_admin" || myRole === "org_admin";

  const [orgFilter, setOrgFilter] = useState("");
  const rulesQuery = usePricingRules(isPencilOutAdmin && orgFilter ? orgFilter : null);
  const orgsQuery = useOrganizations();
  const [creating, setCreating] = useState(false);
  const [confirmingSeedMissing, setConfirmingSeedMissing] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const seedMissing = useSeedMissingPricingRuleDefaults();
  const resetToDefaults = useResetPricingRulesToDefaults();
  // Result toast: surfaces the count after the mutation lands.
  // ``message`` is shown inline above the rules grid; cleared on
  // next mutation or when the user clicks dismiss.
  const [resultBanner, setResultBanner] = useState<
    | { kind: "added"; alreadyPresent: number; added: number }
    | { kind: "reset"; deleted: number; seeded: number }
    | { kind: "error"; message: string }
    | null
  >(null);

  // Defaults seeding is staff-only — contractors never reach for
  // these buttons in the normal flow. Backend enforces the same
  // role check; this is just so the UI doesn't show a button that
  // would 403 when clicked.
  const seedTargetOrgId = orgFilter || undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="font-mono text-2xl font-bold tracking-tight">Pricing rules</h1>
            <HelpPopover title="What pricing rules do">
              <p>
                Your prices drive every quote PencilOut generates. What's here is the authoritative
                source — not Home Depot, not industry averages.
              </p>
              <p>
                Each rule sets a baseline price + tier multiplier (builder, mid, premium, luxury) +
                markup. PencilOut multiplies quantity × baseline × tier × markup to produce each
                line item.
              </p>
              <p>
                The default seeds get you started. Tune them for your actual market once; reuse
                forever.
              </p>
            </HelpPopover>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
            The contractor's own pricing knowledge — per category, per build tier — is the
            authoritative source PencilOut uses to draft quotes. Captured here so quotes are
            grounded in actual rates, not generic data.
          </p>
        </div>
        {canManage && !creating && (
          <div className="flex items-center gap-2">
            {/* Defaults-seeding is staff-only. Backend gates with
                pencilout_admin role; we hide the buttons in the
                contractor view to avoid a clickable 403. */}
            {isPencilOutAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmingSeedMissing(true)}
                  disabled={seedMissing.isPending || resetToDefaults.isPending || !seedTargetOrgId}
                  className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-paper disabled:opacity-50"
                  title={
                    seedTargetOrgId
                      ? "Add default rules that aren't already on file. Preserves contractor edits."
                      : "Pick an organization in the filter to enable."
                  }
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden /> Add missing defaults
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingReset(true)}
                  disabled={seedMissing.isPending || resetToDefaults.isPending || !seedTargetOrgId}
                  className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-burnt-orange transition hover:bg-paper disabled:opacity-50"
                  title={
                    seedTargetOrgId
                      ? "Wipe all rules and re-seed defaults. Destructive — loses any contractor edits."
                      : "Pick an organization in the filter to enable."
                  }
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reset to defaults
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> New rule
            </button>
          </div>
        )}
      </div>

      {resultBanner && (
        <div
          className={`flex items-start justify-between gap-3 rounded border px-3 py-2 text-sm ${
            resultBanner.kind === "error"
              ? "border-red-pencil/40 bg-red-pencil/10 text-red-pencil"
              : "border-olive/40 bg-olive/10 text-graphite"
          }`}
        >
          <div>
            {resultBanner.kind === "added" && (
              <>
                <strong>Added {resultBanner.added} new rules.</strong> {resultBanner.alreadyPresent}{" "}
                were already on file (untouched).
              </>
            )}
            {resultBanner.kind === "reset" && (
              <>
                <strong>Reset complete.</strong> Deleted {resultBanner.deleted}; seeded{" "}
                {resultBanner.seeded} default rules.
              </>
            )}
            {resultBanner.kind === "error" && <>{resultBanner.message}</>}
          </div>
          <button
            type="button"
            onClick={() => setResultBanner(null)}
            className="text-graphite-soft hover:text-graphite"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {confirmingSeedMissing && (
        <ConfirmDialog
          title="Add missing default rules?"
          body={
            <>
              This adds any default rules that aren't already on the org (new categories, missing
              tiers, etc.). The contractor's existing rules — including any markup or baseline
              tweaks — stay exactly as they are. Safe to run more than once.
            </>
          }
          confirmLabel="Add missing defaults"
          busy={seedMissing.isPending}
          onCancel={() => setConfirmingSeedMissing(false)}
          onConfirm={() => {
            seedMissing.mutate(
              { organizationId: seedTargetOrgId },
              {
                onSuccess: (data) => {
                  setConfirmingSeedMissing(false);
                  setResultBanner({
                    kind: "added",
                    alreadyPresent: data.already_present,
                    added: data.added,
                  });
                },
                onError: (err) => {
                  setConfirmingSeedMissing(false);
                  setResultBanner({
                    kind: "error",
                    message:
                      err instanceof ApiError ? err.detail : "Could not seed missing defaults.",
                  });
                },
              },
            );
          }}
        />
      )}

      {confirmingReset && (
        <ConfirmDialog
          title="Reset to default rules?"
          variant="danger"
          body={
            <>
              <strong>Destructive.</strong> This deletes <strong>every</strong> pricing rule for
              this organization and re-seeds the canonical defaults. Any markup, baseline, or
              category edits the contractor has made will be lost. If you only want to fill in
              missing categories, use <em>Add missing defaults</em> instead.
            </>
          }
          confirmLabel="Wipe and reset"
          busy={resetToDefaults.isPending}
          onCancel={() => setConfirmingReset(false)}
          onConfirm={() => {
            resetToDefaults.mutate(
              { organizationId: seedTargetOrgId },
              {
                onSuccess: (data) => {
                  setConfirmingReset(false);
                  setResultBanner({
                    kind: "reset",
                    deleted: data.deleted,
                    seeded: data.seeded,
                  });
                },
                onError: (err) => {
                  setConfirmingReset(false);
                  setResultBanner({
                    kind: "error",
                    message:
                      err instanceof ApiError ? err.detail : "Could not reset pricing rules.",
                  });
                },
              },
            );
          }}
        />
      )}

      {canManage && creating && (
        <PricingRuleForm
          mode="create"
          isPencilOutAdmin={isPencilOutAdmin}
          orgs={orgsQuery.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}

      {isPencilOutAdmin && orgsQuery.data && orgsQuery.data.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="label-mono">Filter by org:</span>
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="rounded border border-rule bg-paper-warm px-2 py-1 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            <option value="">All organizations</option>
            {orgsQuery.data.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Learning loop — surfaces baseline suggestions derived
          from contractor edits to AI-generated lines. Only renders
          when there are pending suggestions; quiet rules don't
          clutter the page. Hidden from org_member (read-only role)
          since they can't apply / dismiss anyway. */}
      {canManage && (
        <LearningSuggestionsPanel
          orgFilter={isPencilOutAdmin && orgFilter ? orgFilter : null}
        />
      )}

      {rulesQuery.isLoading && <p className="text-sm text-graphite-soft">Loading pricing rules…</p>}

      {rulesQuery.error && !rulesQuery.data && (
        <p className="text-sm text-red-pencil">
          {rulesQuery.error instanceof ApiError
            ? rulesQuery.error.detail
            : "Could not load pricing rules."}
        </p>
      )}

      {rulesQuery.data && rulesQuery.data.length === 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Tags className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">
            {canManage
              ? "No pricing rules yet. Add ones for the categories you quote most often (cabinets, flooring, labor) — even rough rates help PencilOut ground its drafts."
              : "No pricing rules captured yet. An admin needs to enter them."}
          </p>
        </div>
      )}

      {rulesQuery.data && rulesQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
          <table className="w-full">
            <thead className="border-b border-rule bg-paper">
              <tr>
                <th className="label-mono px-4 py-2 text-left">Category</th>
                <th className="label-mono px-4 py-2 text-left">Tier</th>
                <th className="label-mono px-4 py-2 text-right">Baseline</th>
                <th className="label-mono px-4 py-2 text-right">Multiplier</th>
                <th className="label-mono px-4 py-2 text-right">Markup</th>
                <th className="label-mono px-4 py-2 text-left">Unit</th>
                {isPencilOutAdmin && <th className="label-mono px-4 py-2 text-left">Org</th>}
                <th className="label-mono px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rulesQuery.data.map((rule) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  isPencilOutAdmin={isPencilOutAdmin}
                  canManage={canManage}
                  orgs={orgsQuery.data ?? []}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RuleRow({
  rule,
  isPencilOutAdmin,
  canManage,
  orgs,
}: {
  rule: PricingRuleResponse;
  isPencilOutAdmin: boolean;
  canManage: boolean;
  orgs: OrganizationSummary[];
}) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteRule = useDeletePricingRule();

  if (editing) {
    return (
      <tr>
        <td colSpan={isPencilOutAdmin ? 8 : 7} className="px-4 py-3">
          <PricingRuleForm
            mode="edit"
            rule={rule}
            isPencilOutAdmin={isPencilOutAdmin}
            orgs={orgs}
            onClose={() => setEditing(false)}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-rule last:border-0 hover:bg-pencil-yellow-soft">
      <td className="px-4 py-3 font-medium text-graphite">{rule.category}</td>
      <td className="px-4 py-3 font-mono text-xs text-graphite-soft">
        {TIER_LABELS[rule.tier as PricingTier] ?? rule.tier}
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">
        {formatCents(rule.baseline_cents)}
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums text-graphite-soft">
        {rule.multiplier.toFixed(2)}×
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums text-graphite-soft">
        {(rule.markup_pct * 100).toFixed(0)}%
      </td>
      <td className="px-4 py-3 font-mono text-xs text-graphite-soft">{rule.unit}</td>
      {isPencilOutAdmin && (
        <td className="px-4 py-3 text-graphite-soft">{rule.organization_name ?? "—"}</td>
      )}
      <td className="px-4 py-3 text-right">
        {canManage ? (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              title="Edit rule"
              className="rounded p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={deleteRule.isPending}
              title="Delete rule"
              className="rounded p-1.5 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        ) : (
          <span className="font-mono text-xs text-graphite-faint">{shortId(rule.id)}</span>
        )}

        {confirmingDelete && (
          <ConfirmDialog
            title={`Delete ${rule.category}?`}
            body={
              <>
                Removes this <span className="font-mono text-graphite">{rule.tier}</span> rule.
                Quotes already drafted with it stay intact, but new quotes won't see it. Prefer
                archiving (set an end date) if you want history preservation.
                {deleteRule.error && (
                  <span className="mt-2 block text-red-pencil">
                    {deleteRule.error instanceof ApiError
                      ? deleteRule.error.detail
                      : "Could not delete."}
                  </span>
                )}
              </>
            }
            confirmLabel="Delete rule"
            variant="danger"
            busy={deleteRule.isPending}
            onConfirm={() => {
              deleteRule.mutate(rule.id, {
                onSettled: () => setConfirmingDelete(false),
              });
            }}
            onCancel={() => {
              setConfirmingDelete(false);
              deleteRule.reset();
            }}
          />
        )}
      </td>
    </tr>
  );
}

function PricingRuleForm({
  mode,
  rule,
  isPencilOutAdmin,
  orgs,
  onClose,
}: {
  mode: "create" | "edit";
  rule?: PricingRuleResponse;
  isPencilOutAdmin: boolean;
  orgs: OrganizationSummary[];
  onClose: () => void;
}) {
  const create = useCreatePricingRule();
  const update = useUpdatePricingRule(rule?.id ?? "");
  const mutation = mode === "create" ? create : update;

  const [orgId, setOrgId] = useState(rule?.organization_id ?? "");
  const [category, setCategory] = useState(rule?.category ?? "");
  const [tier, setTier] = useState<PricingTier>(rule?.tier ?? "mid");
  const [unit, setUnit] = useState(rule?.unit ?? "ea");
  const [baselineDollars, setBaselineDollars] = useState(
    rule ? (rule.baseline_cents / 100).toFixed(2) : "",
  );
  const [multiplier, setMultiplier] = useState(rule?.multiplier ?? 1.0);
  const [markupPct, setMarkupPct] = useState(rule?.markup_pct ?? 0.25);
  const [notes, setNotes] = useState(rule?.notes ?? "");

  // Material/labor split (Tier-2 from dad's-feedback build). Off by
  // default — the contractor opts in by checking "Break out into
  // material + labor". When on, both fields are required and must
  // sum to baseline. Editing a rule that already has a split lands
  // in the on state automatically.
  const initialHasSplit =
    rule != null && rule.material_cents != null && rule.labor_cents != null;
  const [splitEnabled, setSplitEnabled] = useState(initialHasSplit);
  const [materialDollars, setMaterialDollars] = useState(
    rule?.material_cents != null ? (rule.material_cents / 100).toFixed(2) : "",
  );
  const [laborDollars, setLaborDollars] = useState(
    rule?.labor_cents != null ? (rule.labor_cents / 100).toFixed(2) : "",
  );

  const baselineCents = Math.round(Number(baselineDollars || "0") * 100);
  const materialCents = Math.round(Number(materialDollars || "0") * 100);
  const laborCents = Math.round(Number(laborDollars || "0") * 100);
  const splitSum = materialCents + laborCents;
  const splitBalanced = splitEnabled ? splitSum === baselineCents : true;

  const needsOrg = mode === "create" && isPencilOutAdmin;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!category.trim() || baselineCents < 0) return;
    if (needsOrg && !orgId) return;

    if (splitEnabled && !splitBalanced) return;

    try {
      if (mode === "create") {
        await create.mutateAsync({
          organization_id: needsOrg ? orgId : undefined,
          category: category.trim(),
          tier,
          unit: unit.trim(),
          baseline_cents: baselineCents,
          material_cents: splitEnabled ? materialCents : null,
          labor_cents: splitEnabled ? laborCents : null,
          multiplier,
          markup_pct: markupPct,
          notes: notes.trim() || null,
        });
      } else {
        // -1 sentinel clears the split server-side; only send when
        // an existing rule HAD a split + the contractor turned it
        // off (otherwise omit the fields).
        const editingHadSplit = initialHasSplit;
        const splitFields = splitEnabled
          ? { material_cents: materialCents, labor_cents: laborCents }
          : editingHadSplit
            ? { material_cents: -1, labor_cents: -1 }
            : {};
        await update.mutateAsync({
          category: category.trim(),
          tier,
          unit: unit.trim(),
          baseline_cents: baselineCents,
          ...splitFields,
          multiplier,
          markup_pct: markupPct,
          notes: notes.trim() || null,
        });
      }
      onClose();
    } catch {
      /* surfaced below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">
          {mode === "create" ? "New pricing rule" : "Edit pricing rule"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {needsOrg && (
          <label className="block md:col-span-3">
            <span className="label-mono">Organization</span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              <option value="">— select —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block md:col-span-2">
          <span className="label-mono">Category</span>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="cabinets, flooring, labor:plumber, …"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Tier</span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as PricingTier)}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            {Object.entries(TIER_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="label-mono">Baseline ($/unit)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={baselineDollars}
            onChange={(e) => setBaselineDollars(e.target.value)}
            placeholder="450.00"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
          <label className="mt-1 inline-flex items-center gap-1.5 font-mono text-[11px] text-graphite-soft">
            <input
              type="checkbox"
              checked={splitEnabled}
              onChange={(e) => setSplitEnabled(e.target.checked)}
            />
            Break out into material + labor
          </label>
        </label>

        <label className="block">
          <span className="label-mono">Unit</span>
          <input
            type="text"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="sqft, lf, ea, hr"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Tier multiplier</span>
          <input
            type="number"
            step="0.05"
            min="0"
            value={multiplier}
            onChange={(e) => setMultiplier(Number(e.target.value))}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block">
          <span className="label-mono">Typical markup (%)</span>
          <input
            type="number"
            step="1"
            min="0"
            max="200"
            value={Math.round(markupPct * 100)}
            onChange={(e) => setMarkupPct(Number(e.target.value) / 100)}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
          />
        </label>

        {splitEnabled && (
          <>
            <label className="block">
              <span className="label-mono">Material ($/unit)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={materialDollars}
                onChange={(e) => setMaterialDollars(e.target.value)}
                placeholder="35.00"
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="label-mono">Labor ($/unit)</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={laborDollars}
                onChange={(e) => setLaborDollars(e.target.value)}
                placeholder="65.00"
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
              />
            </label>
            <div className="block md:col-span-1">
              <span className="label-mono">Sum check</span>
              <p
                className={`mt-2 font-mono text-xs ${
                  splitBalanced ? "text-graphite-soft" : "text-red-pencil"
                }`}
              >
                {splitBalanced
                  ? `${formatCents(splitSum)} = ${formatCents(baselineCents)} ✓`
                  : `${formatCents(splitSum)} ≠ ${formatCents(baselineCents)} (must match baseline)`}
              </p>
            </div>
          </>
        )}

        <label className="block md:col-span-3">
          <span className="label-mono">Notes (optional)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Supplier-specific handling, seasonal pricing, …"
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      {mutation.error && (
        <p className="text-sm text-red-pencil">
          {mutation.error instanceof ApiError ? mutation.error.detail : "Could not save rule."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            mutation.isPending ||
            !category.trim() ||
            baselineCents < 0 ||
            (needsOrg && !orgId) ||
            (splitEnabled && !splitBalanced)
          }
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          {mutation.isPending ? "Saving…" : mode === "create" ? "Create rule" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

/** Panel showing pending baseline suggestions derived from
 *  contractor edits to AI-generated lines. The product magic of
 *  PencilOut: every contractor edit feeds back into the rules
 *  the AI uses for the next quote, so quotes get more accurate
 *  with usage instead of staying generic.
 *
 *  Layout: each row is a single rule with current baseline →
 *  suggested baseline + observation count + Apply / Dismiss
 *  buttons. The whole panel hides when there are no pending
 *  suggestions so quiet weeks don't show empty chrome. */
function LearningSuggestionsPanel({
  orgFilter,
}: {
  orgFilter: string | null;
}) {
  const query = useLearningSuggestions(orgFilter);
  const apply = useApplyLearnedBaseline();
  const dismiss = useDismissLearnedObservations();
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const suggestions = query.data?.suggestions ?? [];

  if (query.isLoading || suggestions.length === 0) return null;

  return (
    <section className="rounded-lg border-2 border-pencil-yellow bg-pencil-yellow-soft p-4">
      <div className="flex items-start gap-3">
        <Brain
          className="mt-0.5 h-5 w-5 shrink-0 text-graphite"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <h2 className="font-mono text-base font-bold tracking-tight text-graphite">
            Pricing tuning · {suggestions.length} suggestion
            {suggestions.length === 1 ? "" : "s"}
          </h2>
          <p className="mt-1 text-sm text-graphite-soft">
            Your edits to AI-generated lines are clustering around
            new baselines. Apply to lock the change in for future
            quotes, or dismiss to clear the buffer.
          </p>
        </div>
      </div>

      {actionError && (
        <p className="mt-3 rounded border border-red-pencil bg-paper px-3 py-2 text-sm text-red-pencil">
          {actionError}
        </p>
      )}

      <ul className="mt-4 space-y-2">
        {suggestions.map((s) => (
          <SuggestionRow
            key={s.rule_id}
            suggestion={s}
            busy={pendingId === s.rule_id}
            onApply={() => {
              setActionError(null);
              setPendingId(s.rule_id);
              apply.mutate(s.rule_id, {
                onSettled: () => setPendingId(null),
                onError: (err) =>
                  setActionError(
                    err instanceof ApiError
                      ? err.detail
                      : "Could not apply baseline.",
                  ),
              });
            }}
            onDismiss={() => {
              setActionError(null);
              setPendingId(s.rule_id);
              dismiss.mutate(s.rule_id, {
                onSettled: () => setPendingId(null),
                onError: (err) =>
                  setActionError(
                    err instanceof ApiError
                      ? err.detail
                      : "Could not dismiss observations.",
                  ),
              });
            }}
          />
        ))}
      </ul>
    </section>
  );
}

/** One suggestion row — current → suggested baseline plus the
 *  observation context (count, accepted-quote subset, age range)
 *  the contractor needs to decide. */
function SuggestionRow({
  suggestion: s,
  busy,
  onApply,
  onDismiss,
}: {
  suggestion: LearningSuggestion;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const droppedOutliers = s.raw_observation_count - s.observation_count;
  const direction = s.delta_cents >= 0 ? "+" : "−";
  const absDelta = Math.abs(s.delta_cents);
  const oldest = new Date(s.oldest_observation_at);
  const newest = new Date(s.newest_observation_at);
  const ageRangeDays = Math.max(
    1,
    Math.round((newest.getTime() - oldest.getTime()) / 86_400_000),
  );

  return (
    <li className="rounded border border-rule bg-paper p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite">
              {s.category}
            </span>
            <span className="font-mono text-xs uppercase tracking-wider text-graphite-soft">
              · {s.tier} · per {s.unit}
            </span>
          </div>
          <p className="mt-2 text-sm text-graphite">
            <span className="font-mono">
              {formatCents(s.current_baseline_cents)}
            </span>
            <span className="mx-2 text-graphite-soft">→</span>
            <span className="font-mono font-semibold">
              {formatCents(s.suggested_baseline_cents)}
            </span>
            <span className="ml-2 font-mono text-xs text-graphite-soft">
              ({direction}
              {formatCents(absDelta)})
            </span>
          </p>
          <p className="mt-1 font-mono text-xs text-graphite-soft">
            {s.observation_count} edit
            {s.observation_count === 1 ? "" : "s"}
            {s.accepted_observation_count > 0 && (
              <>
                {" · "}
                {s.accepted_observation_count} on signed quote
                {s.accepted_observation_count === 1 ? "" : "s"}
              </>
            )}
            {droppedOutliers > 0 && (
              <>
                {" · dropped "}
                {droppedOutliers} outlier{droppedOutliers === 1 ? "" : "s"}
              </>
            )}
            {" · "}
            {ageRangeDays === 1
              ? "today"
              : `over ${ageRangeDays} days`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-rule bg-paper-warm px-2.5 py-1 font-mono text-xs font-semibold text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-50"
          >
            <X className="h-3 w-3" aria-hidden />
            Dismiss
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-graphite bg-graphite px-2.5 py-1 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            <Check className="h-3 w-3" aria-hidden />
            {busy ? "Working…" : "Apply"}
          </button>
        </div>
      </div>
    </li>
  );
}
